import { DateTime, ExprCall } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { EncFilterField, Encoding, EncodingResult, VariableType } from '@mode-vegas/vegas';
import { SelectedChartService } from '@mode/charts/contract';
import {
  ConditionalFormattingModalComponentFacade,
  DataLimitsAction,
  DataLimitsModalComponent,
  DataLimitsModalData,
  FilterModalService,
  TextFormattingModalService,
} from '@mode/charts/feature-encoding';
import { DataLimitsResults, toChartDataError } from '@mode/charts/ui';
import { ChartDataError, getFacetSizes, getVegasChartDataErrorMsg } from '@mode/charts/util-muze';
import { ChartFormatHelpers, EncodingHelpers, RenderDataModel, TableEncodingHelpers } from '@mode/charts/util-vegas';
import { EmbedActionType, EmbedActions } from '@mode/reports/sandbox/contract';
import {
  ChartTypes,
  ConditionalFormattingTypes,
  DataProfilingTypes,
  FlamingoTypes,
  InterActions,
  SortSettingsTypes,
} from '@mode/shared/contract-common';
import { DialogService, SortSettingsModalDialogService } from '@mode/shared/ui';
import { debounceResize } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledAttr, CompiledField, CompiledFormula, FormulaOperations, Switch } from '@mode/shared/util-switch';
import { DataViewFacade } from '@mode/switch-views/contract';
import { fromFlamingoSelectors } from '@mode/switch-views/domain';
import * as angular from 'angular';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ReportApiService } from '~/services/report-api-service';
import { AppStore } from '~/types/main.types';
import { ChartDesignerActionType } from '../../../../actions/chart-designer/designer.actions';
import { MixableBaseController } from '../../../../common/component';
import { getChartDataErrorMsg } from '../../../../lib/charts/chart-data';
import { ChartDesignerSelectors } from '../../../../selectors/designer.selectors';
import { FeatureToggleService } from '../../../../services/feature-toggle-service';
import { FlamingoService } from '../../../../services/flamingo-service';
import ViewService from '../../../../services/view-service';
import { ChartObservables, DesignerErrorType } from '../../../../types/viz-designer.types';

interface LastRenderedState {
  canRender: boolean;
  isCompleteEncoding: boolean;
  isValid: boolean;
}

class DesignerRendererController extends MixableBaseController {
  static $inject = [
    '$scope',
    'RxStoreService',
    'ViewService',
    'FlamingoService',
    'FeatureToggleService',
    'ReportApiService',
    'DialogService',
    'AnalyticsService',
    'DataViewFacade',
    '$window',
    '$timeout',
    'FilterModalService',
    'SortSettingsModalDialogService',
    'ConditionalFormattingModalComponentFacade',
    'TextFormattingModalService',
  ];

  // bindings
  readOnly: boolean;
  objectContext: ChartTypes.ObjectType;
  observables: ChartObservables;
  private chartService: SelectedChartService;
  private isViewPage = false;

  // local state
  token$: Observable<string>;
  private content$: Observable<FlamingoTypes.RecordsResult['content']>;
  private columns$: Observable<FlamingoTypes.RecordsResult['columns']>;
  private activeTableColumn$: Observable<string>;
  private queryRunToken: string;
  private appendToSelector: string;

  private encoding$: Observable<Encoding>;
  private format$: Observable<ChartTypes.ChartFormat>;
  private fieldFormats$: Observable<ChartTypes.FieldFormatMap>;
  private fieldColors$: Observable<ChartTypes.FieldColorMap>;
  private facetSizes$: Observable<ChartTypes.FacetSizeMap>;
  private chartType$: Observable<ChartTypes.ChartType>;
  private model$: Observable<FlamingoTypes.ModeModel>;
  private view$: Observable<FlamingoTypes.ModeView>;
  private renderModel$: Observable<RenderDataModel>;
  private paging$: Observable<FlamingoTypes.PagingResult>;
  private isValid$: Observable<boolean>;
  private isValidChart$: Observable<boolean>;
  private isReady$: Observable<boolean>;
  private isCompleteEncoding$: Observable<boolean>;
  private canRender$: Observable<boolean>;
  private vizTitle$: Observable<string>;
  private caption$: Observable<ChartTypes.CaptionInfo>;
  private hasTopCaption$: Observable<boolean>;
  private runFailed$: Observable<boolean>;
  private showTableRenderer$: Observable<boolean>;
  private showChartRenderer$: Observable<boolean>;
  private showVegasRenderer$: Observable<boolean>;
  private showEmptyChartState$: Observable<boolean>;
  private isPurged$: Observable<boolean>;
  private isCurrentOrLastRenderable$: Observable<boolean>;
  private isCurrentOrLastRenderedValid$: Observable<boolean>;
  private isCurrentOrLastRenderedComplete$: Observable<boolean>;
  private granularityInfo$: Observable<{ isAllowed: boolean; isEnabled: boolean; isHidden: boolean }>;
  private dataProfileInfo$: Observable<{ isHidden: boolean; isEnabled: boolean }>;

  private drilldownEnabled$: Observable<boolean>;
  private canChangeHierarchy$: Observable<boolean>;

  private canGoUp$: Observable<boolean>;
  private canGoDown$: Observable<boolean>;
  private canUndo$: Observable<boolean>;
  // These observables track the auto/manual apply state
  private isAutoUpdateDisabled$: Observable<boolean>;
  private manuallyApplyChanges$: Observable<boolean>;
  private unappliedChangeCount$: Observable<number>;
  private isAutoOrManualRender$: Observable<boolean>;

  private lastRenderedState: LastRenderedState | undefined;
  private isRendered: boolean;

  private isHighCardinalityModalOpen = false;

  isRenderingChart: boolean;
  isLongRender: boolean;
  renderError: string;
  renderErrorHelp: string;
  planLoading$: Observable<boolean>;
  binSizePlanLoading$: Observable<boolean>;
  planPageLoading$: Observable<boolean>;
  planSucceeded$: Observable<boolean>;
  planFailed$: Observable<boolean>;
  fieldColors: ChartTypes.FieldColorMap;
  format: ChartTypes.ChartFormat;

  showChartLoading$: Observable<boolean>;
  renderContext = 'designer';

  constructor(
    private $scope: angular.IScope,
    private rxStore: AppStore,
    private viewService: ViewService,
    _flamingoService: FlamingoService, // for side-effects
    featureService: FeatureToggleService,
    private reportApiService: ReportApiService,
    private dialogService: DialogService,
    private analyticsService: AnalyticsService,
    private dataViewFacade: DataViewFacade,
    private $window: Window,
    private $timeout: angular.ITimeoutService,
    private filterModalService: FilterModalService,
    private sortSettingsDialogService: SortSettingsModalDialogService,
    private conditionalFormattingModalComponentFacade: ConditionalFormattingModalComponentFacade,
    private textFormattingModalService: TextFormattingModalService
  ) {
    super();

    this.drilldownEnabled$ = featureService.featureDrilldownEnabled$;
  }

  $onInit() {
    this.token$ = this.observables.token$;
    this.isValid$ = this.observables.isValid$;
    const planId$ = this.observables.planId$;
    this.model$ = this.observables.model$;
    this.view$ = this.observables.view$;
    this.runFailed$ = this.observables.runFailed$;

    const chartState$ = this.observables.chartState$;
    this.canGoUp$ = chartState$.pipe(map((c) => c.canGoUp));
    this.canGoDown$ = chartState$.pipe(map((c) => c.canGoDown));
    this.canUndo$ = chartState$.pipe(map((c) => c.canUndo));
    this.canChangeHierarchy$ = combineLatest(this.canGoDown$, this.canGoUp$, (down, up) => down || up);

    this.isReady$ = planId$.pipe(
      switchMap(({ planId }) => this.rxStore.select(fromFlamingoSelectors.createReadySelector(planId))),
      map((ready) => ready)
    );

    this.isValidChart$ = combineLatest(this.isReady$, this.isValid$).pipe(map(([ready, isValid]) => ready && isValid));

    this.isCompleteEncoding$ = this.observables.isComplete$;
    this.canRender$ = this.observables.canRender$;

    // Chart Stuff
    this.chartType$ = this.observables.chartType$;
    this.format$ = this.observables.format$;
    this.fieldFormats$ = this.observables.fieldFormats$;
    this.fieldColors$ = this.observables.fieldColors$;
    this.facetSizes$ = this.observables.facetSizes$;
    this.vizTitle$ = this.observables.title$;
    this.caption$ = this.observables.caption$;
    this.encoding$ = this.observables.encoding$;
    this.renderModel$ = this.observables.renderModel$;

    this.planLoading$ = this.observables.planLoading$;
    this.binSizePlanLoading$ = this.observables.binSizePlanLoading$;
    this.planSucceeded$ = this.observables.planSucceeded$;
    this.planFailed$ = this.observables.planFailed$;

    this.unappliedChangeCount$ = this.observables.unappliedChangesCount$;
    this.isAutoUpdateDisabled$ = this.observables.isAutoUpdateDisabled$;
    this.manuallyApplyChanges$ = this.observables.manuallyApplyChanges$;

    this.observables.fieldColors$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fieldColors: ChartTypes.FieldColorMap) => (this.fieldColors = fieldColors));

    this.observables.format$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((format: ChartTypes.ChartFormat) => (this.format = format));

    // Reset isRendered when the chart type changes.
    this.chartType$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.isRendered = false;
      this.lastRenderedState = undefined;
    });

    // This observable represents the current render state. It is true if auto-update is ON or if the user
    // just applied changes manually.
    this.isAutoOrManualRender$ = this.observables.isAutoOrManualRender$;
    this.unappliedChangeCount$
      .pipe(takeUntil(this.destroyed$), withLatestFrom(this.isAutoOrManualRender$))
      .subscribe(([count, isAutoOrManualRender]) => {
        // Clear active column selection when there are unapplied changes.
        if (count > 0 || (!isAutoOrManualRender && !this.isRendered)) {
          this.setColumnSelection('');
        }
      });

    this.showChartLoading$ = combineLatest(
      this.isCompleteEncoding$,
      this.isAutoOrManualRender$,
      this.isValid$,
      this.planLoading$,
      this.planSucceeded$,
      this.planFailed$,
      this.binSizePlanLoading$
    ).pipe(
      map(
        ([isComplete, isAutoOrManualRender, isValid, planLoading, planSucceeded, planFailed, binSizePlanLoading]) =>
          isComplete &&
          isAutoOrManualRender &&
          isValid &&
          (binSizePlanLoading || planLoading || (!planSucceeded && !planFailed))
      )
    );

    // Table Stuff
    // TODO - Pull this up to observable creators and handle appropriately for GF context (via facades).
    const records$ = planId$.pipe(
      switchMap(({ planId }) => this.rxStore.select(fromFlamingoSelectors.createRecordsSelector(planId))),
      filter((res) => res != null)
    );

    this.paging$ = this.observables.paging$;

    this.columns$ = this.observables.columns$.pipe(takeUntil(this.destroyed$));

    this.content$ = records$.pipe(map((records) => records.content));

    // TODO - Decide how to handle this behavior, possibly plan metadata feature as an active column can
    //   exist in the view context as well, it's not really designer related.
    this.activeTableColumn$ = this.rxStore.select(ChartDesignerSelectors.getActiveTableColumn);

    // TODO - Pull this selector up to observable creators and handle appropriately for gf context.
    this.observables.queryToken$
      .pipe(
        switchMap((queryToken) => this.dataViewFacade.createQueryRunTokenByQueryToken(queryToken)),
        takeUntil(this.destroyed$)
      )
      .subscribe((queryRunToken) => (this.queryRunToken = queryRunToken));

    this.appendToSelector = this.chartService.readonly ? '.report-view-container' : 'body';

    // Handles the canRender state either using the current state or the last rendered state.
    this.isCurrentOrLastRenderable$ = combineLatest([this.canRender$, this.isAutoOrManualRender$]).pipe(
      map(([canRender, isAutoOrManualRender]) =>
        !isAutoOrManualRender ? (this.lastRenderedState?.canRender ?? false) : canRender
      )
    );

    // Handles the isValid state either using the current state or the last rendered state.
    this.isCurrentOrLastRenderedValid$ = combineLatest([this.isValid$, this.isAutoOrManualRender$]).pipe(
      map(([isValid, isAutoOrManualRender]) =>
        !isAutoOrManualRender ? (this.lastRenderedState?.isValid ?? false) : isValid
      )
    );

    // Handles the isCompleteEncoding state either using the current state or the last rendered state.
    this.isCurrentOrLastRenderedComplete$ = combineLatest([this.isCompleteEncoding$, this.isAutoOrManualRender$]).pipe(
      map(([isCompleteEncoding, isAutoOrManualRender]) =>
        !isAutoOrManualRender ? (this.lastRenderedState?.isCompleteEncoding ?? false) : isCompleteEncoding
      )
    );

    this.showTableRenderer$ = combineLatest(
      this.isValid$,
      this.chartType$,
      this.isCurrentOrLastRenderable$,
      this.planLoading$,
      this.runFailed$
    ).pipe(
      map(
        ([isValid, chartType, isCurrentOrLastRenderable, planLoading, runFailed]) =>
          isValid && isCurrentOrLastRenderable && chartType === 'table' && !planLoading && !runFailed
      )
    );

    this.showChartRenderer$ = combineLatest(
      this.isValidChart$,
      this.chartType$,
      this.format$,
      this.isCurrentOrLastRenderable$,
      this.runFailed$
    ).pipe(
      map(
        ([isValid, chartType, format, isCurrentOrLastRenderable, runFailed]) =>
          isValid &&
          chartType !== 'table' &&
          !ChartFormatHelpers.useMuzeRenderer(chartType, format) &&
          isCurrentOrLastRenderable &&
          !runFailed
      )
    );

    this.showVegasRenderer$ = combineLatest(
      this.isValid$,
      this.chartType$,
      this.format$,
      this.isCurrentOrLastRenderable$,
      this.planLoading$,
      this.runFailed$,
      this.showChartLoading$
    ).pipe(
      map(([isValid, chartType, format, isCurrentOrLastRenderable, planLoading, runFailed, showChartLoading]) => {
        const isVegasType = ChartFormatHelpers.useMuzeRenderer(chartType, format);
        return (
          (isValid && isCurrentOrLastRenderable && isVegasType && !planLoading && !runFailed) ||
          (isVegasType && showChartLoading)
        );
      })
    );

    this.isPurged$ = this.observables.isPurged$;

    this.showEmptyChartState$ = this.isCurrentOrLastRenderable$.pipe(
      map((isCurrentOrLastRenderable) => !isCurrentOrLastRenderable && !this.isViewPage)
    );

    this.hasTopCaption$ = this.caption$.pipe(
      map((captionInfo) => !captionInfo.isHidden && captionInfo.position !== ChartTypes.ChartCaptionPosition.Bottom)
    );

    this.granularityInfo$ = combineLatest([
      this.drilldownEnabled$,
      this.canChangeHierarchy$,
      this.format$,
      this.chartType$,
    ]).pipe(
      map(([isDrilldownEnabled, canChangeHierarchy, format, chartType]) => {
        return {
          isAllowed: isDrilldownEnabled && canChangeHierarchy,
          isEnabled: format.drilldownEnabled,
          isHidden: chartType === 'table' || chartType === 'pie' || chartType === 'donut',
        };
      })
    );

    this.dataProfileInfo$ = combineLatest([this.chartType$, this.format$]).pipe(
      map(([chartType, format]) => {
        return {
          isHidden: chartType !== 'table',
          isEnabled: format?.flatTable?.general?.dataProfilingEnabled || false,
        };
      })
    );

    this.isRendered = false;
    // When either isAutoUpdateDisabled or manuallyApplyChanges changes, we need to capture
    // the current chart state.
    combineLatest([this.isAutoUpdateDisabled$, this.manuallyApplyChanges$])
      .pipe(withLatestFrom(this.canRender$, this.isCompleteEncoding$, this.isValid$, this.token$))
      .subscribe(([[isAutoUpdateDisabled, manuallyApplyChanges], canRender, isCompleteEncoding, isValid, token]) => {
        if (isAutoUpdateDisabled || manuallyApplyChanges) {
          this.lastRenderedState = {
            canRender,
            isCompleteEncoding,
            isValid,
          };
        } else {
          this.lastRenderedState = undefined;
        }
      });

    this.renderContext = this.determineRenderContext(this.objectContext, this.isViewPage);
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.objectContext !== undefined && changes.isViewPage != null) {
      this.renderContext = this.determineRenderContext(
        changes.objectContext.currentValue,
        changes.isViewPage.currentValue
      );
    }
  }

  determineRenderContext(objectContext: ChartTypes.ObjectType, isViewPage: boolean) {
    if (objectContext === ChartTypes.ObjectType.Exploration) {
      return isViewPage ? 'exploration_viewer' : 'exploration_designer';
    }
    return isViewPage ? 'report_builder' : 'designer';
  }

  setOffset(offset: number) {
    this.chartService.setOffset(offset);
  }

  setPageSize(pageSize: number) {
    this.chartService.setPageSize(pageSize);
  }

  updateFormat(format: ChartTypes.ChartFormat) {
    this.chartService.updateChartFormat(format);
  }

  updateFieldFormat(args: { formula: string; textFormat: ChartTypes.TextFormat }) {
    this.chartService.updateTextFormat(args.formula, ChartTypes.FieldPaneTypes.Default, args.textFormat);
  }

  /**
   * Call the configuration filter modal with necessary parameters to add or edit the chosen filter pill.
   * @TODO: Unused now, but will be used in followup PR when swapping out the implementation.
   */
  openFieldFormatEditModal(args: { columnId: string; fieldFormats: ChartTypes.FieldFormatMap; encoding: Encoding }) {
    this.openTextFormatModal(args);
  }

  openTextFormatModal(args: { columnId: string; fieldFormats: ChartTypes.FieldFormatMap; encoding: Encoding }) {
    const { columnId, fieldFormats, encoding } = args;
    const format = fieldFormats[args.columnId]?.paneFormat?.[ChartTypes.FieldPaneTypes.Default];
    this.view$.pipe(first()).subscribe((view) => {
      const compiled = CompiledField.fromFormula(view, columnId, Attr.Type.Discrete);
      this.textFormattingModalService.open({
        title: columnId.slice(1, -1),
        colType: compiled.formulaDataType!,
        format,
        encoding,
        applyCustomFormat: ({ textFormat }) => {
          this.updateFieldFormat({
            formula: columnId,
            textFormat,
          });
        },
      });
    });
  }

  openConditionalFormattingModal(args: { columnId: string; encoding: Encoding }) {
    // open conditional formatting modal.
    const { columnId, encoding } = args;
    this.conditionalFormattingModalComponentFacade.open({
      observables: this.observables,
      encoding,
      selectedColumn: columnId,
      fieldColors: this.fieldColors,
      format: this.format,
      applyAction: (colors: ConditionalFormattingTypes.Color[]) => {
        let selectedColors = colors.reduce((acc, color) => ({ ...acc, [color.selected.value]: color }), {});

        const encodingResult = EncodingHelpers.loadEncoding(encoding);
        const formatColumns = this.format.flatTable.columns.map((col) => {
          if (selectedColors[col.id]) {
            return {
              ...col,
              conditionalFormatting: {
                isEnabled: true,
                legendId: selectedColors[col.id].legendId,
                color: selectedColors[col.id].color,
                addToText: selectedColors[col.id].addToText,
                selectedColorPalette: selectedColors[col.id].selectedColorPalette,
              },
            };
          } else {
            return {
              ...col,
              conditionalFormatting: {
                isEnabled: false,
              },
            };
          }
        });

        this.chartService.dispatchEncodingAndFormatUpdate(encodingResult, undefined, undefined, {
          ...this.format,
          flatTable: {
            ...this.format.flatTable,
            columns: formatColumns,
          },
        });
      },
    });
  }

  /**
   * Handle update encoding coming from child table.
   */
  updateEncoding(encResult: EncodingResult) {
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  replaceLastEncoding(encResult: EncodingResult) {
    this.chartService.dispatchReplaceChartHistory(encResult.encoding, undefined);
  }

  setToManualUpdateInHistory() {
    this.chartService.dispatchReplaceChartHistory(undefined, true);
  }

  selectQuery() {
    this.observables.queryToken$.pipe(first()).subscribe((queryToken) => {
      this.viewService.selectQuery({ token: queryToken }, true);
    });
  }

  /**
   * Set the active column that will be acted upon
   */
  setColumnSelection(column: string) {
    this.rxStore.dispatch({
      type: ChartDesignerActionType.SetSelectedColumn,
      column: column,
    });
  }

  /**
   * Update the tableColumnSearch and triggers re-query
   */
  setTableColumnSearch(args: { token: string; columnSearchModel: ChartTypes.TableColumnSearch['model'] }) {
    this.rxStore.dispatch({
      type: ChartDesignerActionType.SetTableColumnSearch,
      token: args.token,
      columnSearchModel: args.columnSearchModel,
    });
  }

  updateTitleText(newTitle: string, token: string) {
    this.chartService.updateTitle(token, newTitle);
  }

  updateShowTitle(isShowTitle: boolean, currentCaption: ChartTypes.CaptionInfo, token: string) {
    this.chartService.updateCaption(token, { ...currentCaption, isTitleHidden: !isShowTitle });
    this.dispatchChartResize();
  }

  updateCaptionText(text: string, currentCaption: ChartTypes.CaptionInfo, token: string) {
    this.chartService.updateCaption(token, { ...currentCaption, text });
  }

  updateShowCaption(isShowCaption: boolean, currentCaption: ChartTypes.CaptionInfo, token: string) {
    this.chartService.updateCaption(token, { ...currentCaption, isHidden: !isShowCaption });
    this.dispatchChartResize();
  }

  updateCaptionPosition(
    position: ChartTypes.ChartCaptionPosition,
    currentCaption: ChartTypes.CaptionInfo,
    token: string
  ) {
    this.chartService.updateCaption(token, { ...currentCaption, position });
    this.dispatchChartResize();
  }

  updateShowGranularity(enabled: boolean, format: ChartTypes.ChartFormat) {
    this.chartService.updateChartFormat({ ...format, drilldownEnabled: enabled });
  }

  onHeaderSizeChange() {
    this.dispatchChartResize();
  }

  dispatchChartResize() {
    debounceResize(this.$window);
  }

  changeHierarchy(forward: boolean, token: string) {
    const canChange$ = forward ? this.canGoDown$ : this.canGoUp$;
    canChange$
      .pipe(
        first(),
        filter((can) => can)
      )
      .subscribe(() => {
        this.chartService.changeHierarchy(token, forward);
      });
  }

  /**
   * Call the configuration filter modal with necessary parameters to add or edit the chosen filter pill.
   */
  updateFilterFieldEncoding(args: {
    model: FlamingoTypes.ModeModel;
    encoding: Encoding;
    queryToken: string;
    name: string;
  }) {
    const { model, encoding, queryToken, name } = args;
    const encFilterField = encoding.filter.find(
      (filterItem) => TableEncodingHelpers.getColNameFromFilterFormula(model, queryToken, filterItem.formula) === name
    );

    const view = Switch.Views.find(model, queryToken);
    const attr = Switch.Attrs.find(model, queryToken, name);

    // If an encFilterField is present, then a filter already exists on that field and we need to edit the existing one,
    // else, we create a new filter to be added.
    if (encFilterField) {
      this.filterModalService.editFilter(
        this.queryRunToken,
        encFilterField,
        encoding,
        view,
        (filterField: EncFilterField, field: CompiledField) => {
          // Vegas' addFilter is actually upsert
          const result = EncodingHelpers.addFilterToChartEncoding(encoding, filterField, field);
          this.chartService.dispatchEncodingUpdate(result);
        }
      );
    } else {
      // Add a new filter
      if (attr != null) {
        this.filterModalService.addFilter(
          this.queryRunToken,
          CompiledAttr.fromAttr(view, attr),
          encoding,
          view,
          (filterField: EncFilterField, field: CompiledField) => {
            const newEncoding = EncodingHelpers.addFilterToChartEncoding(encoding, filterField, field);
            this.chartService.dispatchEncodingUpdate(newEncoding);
          }
        );
      }
    }
  }

  addAllTableFields(model: FlamingoTypes.ModeModel, queryToken: string) {
    const encoding = TableEncodingHelpers.initFilledEncoding(model, queryToken);
    this.chartService.dispatchEncodingUpdate(encoding);
  }

  convertEmbedActionAndDispatch(action: EmbedActions, token: string) {
    // we're assuming this is called from highcharts, which is not angular-aware and so this code would need to be in an apply block
    this.$scope.$evalAsync(() => {
      // Need to fire explorer actions for readonly mode because explorer state needs to be separate from the selectedReport.charts state
      switch (action.type) {
        case EmbedActionType.ChangeHierarchy:
          this.changeHierarchy(action.forward, token);
          break;
        case EmbedActionType.DrillDown:
          this.chartService.drillDown(action.chartToken, action.target);
          break;
        case EmbedActionType.MuzeChartRendered:
          break;
      }
    });
  }

  convertVizEmbedActionAndDispatch(action: EmbedActions) {
    // Need to fire explorer actions for readonly mode because explorer state needs to be separate from the selectedReport.charts state
    switch (action.type) {
      case EmbedActionType.ChangeHierarchy:
        this.changeHierarchy(action.forward, action.chartToken);
        break;
      case EmbedActionType.DrillDown:
        this.chartService.drillDown(action.chartToken, action.target);
        break;
      case EmbedActionType.MuzeChartRendered:
        break;
      case EmbedActionType.DynamicDrillDown:
        this.doDynamicDrilldown(
          action.chartToken,
          action.target,
          action.targetFieldId,
          action.drillToField,
          action.dateTruncUnit
        );
        break;
      case EmbedActionType.AnalyticsEvent:
        this.analyticsService.track(action.name, {
          location: action.location,
          action: action.action,
          context: action.context,
          context_id: action.context_id,
          chartType: action.chartType,
        });
        break;
    }
  }

  doDynamicDrilldown(
    chartToken: string,
    target: ChartTypes.MarkTarget,
    targetFieldId: string,
    drillToField: string,
    dateTruncUnit?: DateTime.Unit
  ) {
    this.chartService.dynamicDrillDown(chartToken, target, targetFieldId, drillToField, dateTruncUnit);
  }

  dispatchAction($event: { componentId: string; action: InterActions.Union }) {
    this.reportApiService.dispatch($event.componentId, $event.action);
  }

  renderStart(isNewCanvas: boolean) {
    this.isRendered = false;
    this.isRenderingChart = true;
    this.isLongRender = false;
    this.renderError = null;
    this.renderErrorHelp = null;
  }

  renderDelay() {
    if (this.isRenderingChart) {
      this.isLongRender = true;
    }
  }

  renderEnd(error: Error) {
    const errorMsgs = error != null ? getChartDataErrorMsg(error) : undefined;
    this.handleRenderEnd(errorMsgs);
  }

  renderEndVegas($event: { error?: Error; options?: any }) {
    const error = $event?.error;
    const errorMsgs = error !== null && error !== undefined ? getVegasChartDataErrorMsg(error) : undefined;
    this.handleRenderEnd(errorMsgs);
  }

  handleRenderEnd(error: { renderError: string; renderErrorHelp: string } | undefined) {
    this.isRendered = true;
    this.isRenderingChart = false;
    this.isLongRender = false;
    this.renderError = undefined;
    this.renderErrorHelp = undefined;
    if (error !== null && error !== undefined) {
      const { renderError, renderErrorHelp } = error;
      this.renderError = renderError;
      this.renderErrorHelp = renderErrorHelp;
    }
  }

  dataLimitsExceeded(results: DataLimitsResults) {
    // Present the user with a modal with options for handling the large data.
    // We only want to do this if:
    //  - we are in the designer/explorer
    //  - The filter modal isn't currently open. We don't want to put a modal over a modal
    //
    if (!this.isViewPage && !this.filterModalService.isFilterModalOpen()) {
      combineLatest([this.canUndo$, this.encoding$, this.view$, this.chartType$])
        .pipe(first())
        .subscribe(([canUndo, encoding, view, chartType]) => {
          if (this.isHighCardinalityModalOpen) {
            return;
          }
          const modalData: DataLimitsModalData = {
            view,
            encoding,
            dataResults: results,
            canUndo,
            canAggregatePositionalChannel: chartType !== 'vegasPivotTable',
            applyAction: this.applyDataLimitsAction.bind(this),
          };
          this.isHighCardinalityModalOpen = true;
          this.dialogService.open({
            component: DataLimitsModalComponent,
            matDialogConfig: {
              backdropClass: 'mode-viz-backdrop',
              disableClose: true,
              data: modalData,
            },
          });
        });
    } else {
      // Treat this as an error and display the message to the user.
      const error = new ChartDataError(results.reason, results.actual, results.limit, results.stats, results.channel);
      this.renderEndVegas({ error });
    }
  }

  applyDataLimitsAction(
    action: DataLimitsAction,
    results: DataLimitsResults,
    channel?: string,
    aggr?: ExprCall.Name,
    compiledAttr?: CompiledFormula
  ) {
    // Send the user choice to Segment
    this.trackHighCardinalityModalResponse(action);

    this.isHighCardinalityModalOpen = false;

    switch (action) {
      case DataLimitsAction.Undo:
        this.renderEndVegas(null);
        this.token$.pipe(first()).subscribe((token) => this.chartService.undo(token));
        break;
      case DataLimitsAction.DisplayError:
        this.renderEndVegas({ error: toChartDataError(results) });
        break;
      case DataLimitsAction.ManualUpdate:
        this.renderEndVegas(null);
        // Trigger the auto-update message
        this.isRendered = false;
        this.setToManualUpdateInHistory();
        break;
      case DataLimitsAction.AutoAggregate:
        this.autoAggregateAction(aggr ?? ExprCall.Name.Count, compiledAttr as CompiledField, channel);
        break;
      case DataLimitsAction.Filter:
        this.openFilterModal(compiledAttr, results);
        break;
      default:
        break;
    }
  }

  autoAggregateAction(aggr: ExprCall.Name, field: CompiledField, channel?: string) {
    combineLatest([this.encoding$, this.view$])
      .pipe(first())
      .subscribe(([encoding, view]) => {
        if (field.isAggregateable || field.hasAggFunc) {
          const summary = EncodingHelpers.getSummary(encoding);
          // Aggregate expression of the underlying attr
          const source = FormulaOperations.aggregate(field, aggr);
          let result: EncodingResult | undefined;
          if (channel != null) {
            if (Object.keys(summary[`${channel}Ids`]).find((key) => key === 'multiple')) {
              result = EncodingHelpers.updateVegasFieldEncoding(view, encoding, {
                id: field.alias,
                formula: source,
                variable: {
                  type: VariableType.CONTINUOUS,
                },
              });
            } else {
              result = EncodingHelpers.updateAllFieldEncoding(encoding, summary[`${channel}Ids`][field.alias], {
                formula: source,
                variable: {
                  type: VariableType.CONTINUOUS,
                },
              });
            }
          } else {
            result = EncodingHelpers.updateVegasFieldEncoding(view, encoding, {
              id: field.alias,
              formula: source,
              variable: {
                type: VariableType.CONTINUOUS,
              },
            });
          }
          if (result !== undefined) {
            this.replaceLastEncoding(result);
          }
        }
      });
  }

  openFilterModal(attrField: CompiledFormula | undefined, results: DataLimitsResults) {
    combineLatest([this.encoding$, this.renderModel$, this.view$])
      .pipe(first())
      .subscribe(([encoding, renderModel, view]) => {
        const existingFilter = encoding.filter.find((f) => {
          const compiled = renderModel.compileEncField(f);
          return compiled.fieldName === attrField.fieldName;
        });
        if (existingFilter) {
          // Edit existing filter
          this.filterModalService.editFilter(
            this.queryRunToken,
            existingFilter,
            encoding,
            view,
            (filterField: EncFilterField, field: CompiledField) => {
              // Vegas' addFilter is actually upsert
              const result = EncodingHelpers.addFilterToChartEncoding(encoding, filterField, field);
              this.replaceLastEncoding(result);
            },
            this.onFilterCancel.bind(this, results)
          );
        } else {
          this.filterModalService.addFilter(
            this.queryRunToken,
            attrField,
            encoding,
            view,
            (filterField: EncFilterField, field: CompiledField) => {
              const newEncoding = EncodingHelpers.addFilterToChartEncoding(encoding, filterField, field);
              this.replaceLastEncoding(newEncoding);
            },
            this.onFilterCancel.bind(this, results)
          );
        }
      });
  }

  onFilterCancel(results: DataLimitsResults) {
    if (!this.isRendered) {
      this.renderEndVegas({ error: toChartDataError(results) });
    } else if (!this.renderError) {
      this.renderEndVegas(null);
    }
  }

  trackHighCardinalityModalResponse(action: DataLimitsAction) {
    combineLatest([this.token$, this.chartType$])
      .pipe(first())
      .subscribe(([token, chartType]) => {
        this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
          location: 'High Cardinality Modal',
          action,
          context: 'visualization',
          context_id: token,
          chartType,
        });
      });
  }

  /**
   * Invokes chart service to update facet sizes from callback.
   *
   * @param $event {any} event
   */
  updateFacetSizes($event) {
    this.chartService.updateFacetSizes(getFacetSizes($event));
  }

  manuallyUpdate() {
    this.chartService.manuallyApplyChanges();

    combineLatest([this.token$, this.chartType$])
      .pipe(first())
      .subscribe(([token, chartType]) => {
        this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
          location: 'Visualization Message',
          action: 'Manual Apply Changes',
          context: 'visualization',
          context_id: token,
          chartType,
        });
      });
  }

  enableAutoUpdates() {
    this.chartService.updateIsAutoUpdateDisabled(false);

    combineLatest([this.token$, this.chartType$])
      .pipe(first())
      .subscribe(([token, chartType]) => {
        this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
          location: 'Visualization Message',
          action: 'Switch to automatic',
          context: 'visualization',
          context_id: token,
          chartType,
        });
      });
  }

  updateShowDataProfiling($event: boolean, format: ChartTypes.ChartFormat) {
    this.chartService.updateChartFormat({
      ...format,
      flatTable: {
        ...format.flatTable,
        general: {
          ...format.flatTable.general,
          dataProfilingEnabled: $event,
        },
      },
    });
  }

  handleGetDataProfiling($event: DataProfilingTypes.DataProfilingChartDetails) {
    this.chartService.getDataProfiling($event);
  }

  onSortSettingsChange($event: SortSettingsTypes.SortSettingsEvent) {
    this.sortSettingsDialogService.open({
      encoding: $event.encoding,
      selectedColumn: $event.selectedColumn,
      descending: $event.descending,
      applyAction: (sorts: SortSettingsTypes.Sort[]) => {
        this.updateEncoding(
          sorts.reduce(
            (encResult, sort) =>
              EncodingHelpers.addTableSort(encResult.encoding, sort.selected.value, sort.descending, false),
            EncodingHelpers.clearSorts($event.encoding)
          )
        );
      },
    });
  }
}

export const DesignerRendererComponent: angular.IComponentOptions = {
  bindings: {
    observables: '<',
    chartService: '<',
    readOnly: '<',
    isViewPage: '<?',
    objectContext: '<',
  },
  template: `
    <mode-chart-options-menu
      ng-if="!ctrl.readOnly && ctrl.objectContext ==='visualization'"
      class="chart-options-menu-button"
      [is-show-caption]="!(ctrl.caption$ | async:this).isHidden"
      [is-show-title]="!(ctrl.caption$ | async:this).isTitleHidden"
      [caption-position]="(ctrl.caption$ | async:this).position"
      [granularity-info]="ctrl.granularityInfo$ | async:this"
      [data-profile-info]="ctrl.dataProfileInfo$ | async:this"
      (show-title-change)="ctrl.updateShowTitle($event, (ctrl.caption$ | async:this), (ctrl.token$ | async:this))"
      (show-caption-change)="ctrl.updateShowCaption($event, (ctrl.caption$ | async:this), (ctrl.token$ | async:this))"
      (caption-position-change)="ctrl.updateCaptionPosition($event, (ctrl.caption$ | async:this), (ctrl.token$ | async:this))"
      (show-granularity-change)="ctrl.updateShowGranularity($event, (ctrl.format$ | async: this))"
      (show-data-profile-change)="ctrl.updateShowDataProfiling($event, (ctrl.format$ | async: this))"
      >
    </mode-chart-options-menu>
    <div ng-if="!ctrl.readOnly && ctrl.objectContext ==='visualization'" class="viz-header"
      ng-class="{ 'has-title': !(ctrl.caption$ | async:this).isTitleHidden,
        'has-top-caption': (ctrl.hasTopCaption$ | async:this)}">
      <div class="viz-title">
        <mode-in-place-input
          ng-if="!(ctrl.caption$ | async:this).isTitleHidden"
          placeholder="Untitled chart"
          [text]="ctrl.vizTitle$ | async:this"
          [maxchars]="250"
          (text-change)="ctrl.updateTitleText($event, ctrl.token$ | async:this)"
          ></mode-in-place-input>
      </div>
    </div>
    <designer-error
      error="${DesignerErrorType.RunFailed}"
      ng-if="(ctrl.runFailed$ | async:this)"
      on-action="ctrl.selectQuery()"
      object-context="ctrl.objectContext"
      chart-type="ctrl.chartType$ | async:this"
      query-token="ctrl.observables.queryToken$ | async:this"
    ></designer-error>
    <div
      ng-if="!(ctrl.runFailed$ | async:this)"
      class="charts-container">
      <div class="viz-caption"
        ng-if="!(ctrl.caption$ | async:this).isHidden && !ctrl.readOnly && ctrl.objectContext ==='visualization'"
        ng-class="{ 'caption-position-bottom': (ctrl.caption$ | async:this).position === 'bottom',
          'has-temporal-drilldown': (ctrl.canChangeHierarchy$ | async:this)}">
        <mode-in-place-textarea
          placeholder="Click to add chart description. Shift-enter for new line."
          [maxchars]="360"
          [text]="(ctrl.caption$ | async:this).text"
          (text-change)="ctrl.updateCaptionText($event, (ctrl.caption$ | async:this), (ctrl.token$ | async:this))"
          (size-change)="ctrl.onHeaderSizeChange()"
        >
        </mode-in-place-textarea>
      </div>
      <mode-table-renderer
        ng-if="ctrl.showTableRenderer$ | async:this"
        [token]="ctrl.token$ | async:this"
        [is-designer]="true"
        [is-view-page]="ctrl.isViewPage"
        [columns]="ctrl.columns$ | async:this"
        [content]="ctrl.content$ | async:this"
        [paging]="ctrl.paging$ | async:this"
        [active-table-column]="ctrl.activeTableColumn$ | async:this"
        [encoding]="ctrl.encoding$ | async:this"
        [field-formats]="ctrl.fieldFormats$ | async:this"
        [format]="ctrl.format$ | async:this"
        [model]="ctrl.model$ | async:this"
        [query-token]="ctrl.observables.queryToken$ | async:this"
        [data-profiling]="ctrl.observables.dataProfiling$ | async:this"
        [conditional-formatting]="ctrl.observables.conditionalFormatting$ | async: this"
        [auto-height]="false"
        [color-palette-token]="ctrl.observables.colorPaletteToken$ | async:this"
        (set-offset)="ctrl.setOffset($event)"
        (set-page-size)="ctrl.setPageSize($event)"
        (update-format)="ctrl.updateFormat($event)"
        (set-column-selection)="ctrl.setColumnSelection($event)"
        (update-encoding)="ctrl.updateEncoding($event)"
        (update-filter-field-encoding)="ctrl.updateFilterFieldEncoding($event)"
        (open-field-format-edit-modal)="ctrl.openFieldFormatEditModal($event)"
        (set-table-column-search)="ctrl.setTableColumnSearch($event)"
        (render-start)="ctrl.renderStart()"
        (render-end)="ctrl.renderEnd()"
        (get-data-profiling)="ctrl.handleGetDataProfiling($event)"
        (handle-sort-settings)="ctrl.onSortSettingsChange($event)"
        (open-conditional-formatting-modal)="ctrl.openConditionalFormattingModal($event)"
      ></mode-table-renderer>
      <!-- Below, ctrl.isViewPage checks mode in explorations. -->
      <mode-viz-renderer
        ng-if="(ctrl.showVegasRenderer$ | async:this)"
        ng-show="!ctrl.renderError"
        [token]="ctrl.token$ | async:this"
        [render-model]="ctrl.renderModel$ | async:this"
        [render-context]="ctrl.renderContext"
        [encoding]="ctrl.encoding$ | async:this"
        [format]="ctrl.format$ | async:this"
        [field-formats]="ctrl.fieldFormats$ | async:this"
        [field-colors]="ctrl.fieldColors$ | async:this"
        [facet-sizes]="ctrl.facetSizes$ | async:this"
        [drilldown-enabled]="ctrl.drilldownEnabled$ | async:this"
        [drill-anywhere-enabled]="true"
        [is-temporal-drill-allowed]="ctrl.canChangeHierarchy$ | async:this"
        [report-api-service]="ctrl.reportApiService"
        [suspend-rendering]="(ctrl.unappliedChangeCount$ | async:this) > 0 || (!(ctrl.isAutoOrManualRender$ | async:this) && !ctrl.isRendered)"
        [chart-type]="ctrl.chartType$ | async:this"
        (render-start)="ctrl.renderStart($event)"
        (render-delay)="ctrl.renderDelay()"
        (render-end)="ctrl.renderEndVegas($event)"
        (data-limits-exceeded)="ctrl.dataLimitsExceeded($event)"
        (chart-action)="ctrl.dispatchAction($event)"
        (embed-action)="ctrl.convertVizEmbedActionAndDispatch($event)"
        (update-facet-sizes)="ctrl.updateFacetSizes($event)">
      </mode-viz-renderer>
      <chart-renderer-too
        token="ctrl.token$ | async:this"
        ng-if="ctrl.showChartRenderer$ | async:this"
        ng-show="!ctrl.renderError"
        is-view-page="ctrl.isViewPage"
        render-model="ctrl.renderModel$ | async:this"
        chart-type="ctrl.chartType$ | async:this"
        render-context="designer"
        drilldown-enabled="ctrl.drilldownEnabled$ | async:this"
        encoding="ctrl.encoding$ | async:this"
        format="ctrl.format$ | async:this"
        suspend-rendering="(ctrl.unappliedChangeCount$ | async:this) > 0 || (!(ctrl.isAutoOrManualRender$ | async:this) && !ctrl.isRendered)"
        on-embed-action="ctrl.convertEmbedActionAndDispatch($action, ctrl.token$ | async:this)"
        render-start="ctrl.renderStart()"
        render-end="ctrl.renderEnd($err)">
      </chart-renderer-too>
      <div ng-if="(ctrl.drilldownEnabled$ | async:this)
        && (ctrl.format$ | async:this).drilldownEnabled
        && (ctrl.canChangeHierarchy$ | async:this)
        && (ctrl.canRender$ | async:this)"
        class="drill-actions"
        ng-class="{ 'viz-explorer-drill-actions': (ctrl.showVegasRenderer$ | async:this)}">
        <button class="drill-action-btn"
          ng-class="{ disabled: !(ctrl.canGoUp$ | async:this) }"
          ng-click="ctrl.changeHierarchy(false, ctrl.token$ | async:this)">
          <svg-icon
            class="icon-hierarchy-up"
            type="'hierarchy-up'"
            uib-tooltip="Decrease Granularity"
            tooltip-append-to-body="true"
            tooltip-placement="top"
            tooltip-class="hierarchy-tooltip">
          </svg-icon>
        </button>
        <button class="drill-action-btn"
          ng-class="{ disabled: !(ctrl.canGoDown$ | async:this)}"
          ng-click="ctrl.changeHierarchy(true, ctrl.token$ | async:this)">
          <svg-icon
            class="icon-hierarchy-down"
            type="'hierarchy-down'"
            uib-tooltip="Increase Granularity"
            tooltip-append-to-body="true"
            tooltip-placement="top"
            tooltip-class="hierarchy-tooltip">
          </svg-icon>
        </button>
      </div>
      <mode-viz-loader class="viz-loader"
        ng-show="ctrl.isRenderingChart || (ctrl.showChartLoading$ | async:this)"
        [show-long-load-time-msg]="ctrl.isLongRender"
      ></mode-viz-loader>
      <p ng-if="(ctrl.isPurged$ | async:this)" class="is-purged">No results</p>
      <empty-chart-state
        ng-if="((ctrl.showEmptyChartState$ | async:this) || (ctrl.planFailed$ | async:this)) && !(ctrl.isPurged$ | async:this)"
        chart-type="ctrl.chartType$ | async:this"
        is-valid="ctrl.isCurrentOrLastRenderedValid$ | async:this"
        is-complete="ctrl.isCurrentOrLastRenderedComplete$ | async:this"
        plan-failed="ctrl.planFailed$ | async:this"
        on-add-all-fields="ctrl.addAllTableFields(ctrl.model$ | async:this, ctrl.observables.queryToken$ | async:this)"
        error="ctrl.observables.planError$ | async:this"
      ></empty-chart-state>
      <viewer-empty-chart
        ng-if="!(ctrl.canRender$ | async:this) && ctrl.isViewPage && !(ctrl.planLoading$ | async:this) && !(ctrl.isPurged$ | async:this)"
        chart-type="ctrl.chartType$ | async:this"
        is-valid="ctrl.isValid$ | async:this"
        is-complete="ctrl.isCompleteEncoding$ | async:this"
        report-edit-link="(ctrl.observables.links$ | async:this)['web_edit'].href"
      ></viewer-empty-chart>
      <div ng-if="ctrl.renderError && (ctrl.canRender$ | async:this)" class="chart-error-container">
        <div class="error-message error-empty-message">
          <div class="message">
            <h2 class="heading" ng-bind="ctrl.renderError" />
            <p ng-bind="ctrl.renderErrorHelp" />
          </div>
        </div>
      </div>
      <mode-auto-update-message
        ng-if="(ctrl.unappliedChangeCount$ | async:this) > 0 || (!(ctrl.isAutoOrManualRender$ | async:this) && !ctrl.isRendered)"
        [is-rendered]="ctrl.isRendered"
        (update-visualization)="ctrl.manuallyUpdate()"
        (set-to-automatic)="ctrl.enableAutoUpdates()">
      </mode-auto-update-message>
    </div>
  `,
  controller: DesignerRendererController,
  controllerAs: 'ctrl',
};
